/**
 * New proposal — Step 1 (select proposal) and Step 2 (choose mode).
 *
 * Everything on the cards comes from the approved template metadata: the
 * description is the template's promise line, and the "best for" guidance is
 * its "Is This the Right Fit?" bullets. Nothing here is written twice.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { endpoints, type ProposalMode, type TemplateSummary } from '../lib/api';
import { useApiErrorHandler } from '../lib/auth';
import { Alert, Button, Skeleton } from '../components/ui';

type Step = 'template' | 'mode';

export function NewProposalPage() {
  const navigate = useNavigate();
  const handleError = useApiErrorHandler();

  const [step, setStep] = useState<Step>('template');
  const [templates, setTemplates] = useState<TemplateSummary[] | null>(null);
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [mode, setMode] = useState<ProposalMode>('QUICK');
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    endpoints
      .templates()
      .then((result) => setTemplates(result.templates))
      .catch((caught) => setError(handleError(caught)));
  }, [handleError]);

  const selected = templates?.find((t) => t.templateId === templateId) ?? null;

  const create = async () => {
    if (!templateId) return;
    setCreating(true);
    setError(null);
    try {
      const result = await endpoints.createProposal(templateId, undefined, mode);
      navigate(`/proposals/${result.proposal.proposalId}`);
    } catch (caught) {
      setError(handleError(caught));
      setCreating(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-ink">New proposal</h1>
        <p className="mt-0.5 text-[13px] text-muted">
          {step === 'template'
            ? 'Choose the engagement type. The approved template supplies the content.'
            : 'Choose how much you want to configure.'}
        </p>
      </div>

      <StepIndicator step={step} />

      {error && <Alert tone="error">{error}</Alert>}

      {step === 'template' ? (
        <>
          {!templates ? (
            <div className="grid gap-4 sm:grid-cols-2">
              {Array.from({ length: 5 }, (_, i) => (
                <Skeleton key={i} className="h-52 w-full" />
              ))}
            </div>
          ) : (
            <ul className="grid gap-4 sm:grid-cols-2">
              {templates.map((template) => (
                <li key={template.templateId}>
                  <TemplateCard
                    template={template}
                    selected={templateId === template.templateId}
                    onSelect={() => setTemplateId(template.templateId)}
                  />
                </li>
              ))}
            </ul>
          )}

          <div className="flex items-center gap-3 border-t border-rule pt-5">
            <Button onClick={() => setStep('mode')} disabled={!templateId}>
              Continue
            </Button>
            <Button variant="secondary" onClick={() => navigate('/')}>
              Cancel
            </Button>
          </div>
        </>
      ) : (
        <>
          {selected && (
            <div className="rounded-lg border border-rule bg-tint px-4 py-3">
              <p className="text-[12px] uppercase tracking-wide text-muted">Selected</p>
              <p className="text-sm font-semibold text-ink">{selected.name}</p>
            </div>
          )}

          <fieldset className="grid gap-4 sm:grid-cols-2">
            <legend className="sr-only">Configuration mode</legend>

            <ModeCard
              value="QUICK"
              current={mode}
              onSelect={setMode}
              title="Quick Proposal"
              recommended
              minutes="2–3 minutes"
              description="Uses approved scope and proposal content. You only supply the client, timing and commercials."
              includes={[
                'Approved scope of work',
                'Approved deliverables and approach',
                'Standard differentiators, assumptions and next steps',
              ]}
            />

            <ModeCard
              value="CUSTOM"
              current={mode}
              onSelect={setMode}
              title="Customize Proposal"
              minutes="10+ minutes"
              description="Allows scope, deliverable and content modifications."
              includes={[
                'Everything in Quick Proposal',
                'Choose which scope items are included',
                'Choose which deliverables and phases apply',
              ]}
            />
          </fieldset>

          <div className="flex items-center gap-3 border-t border-rule pt-5">
            <Button onClick={create} loading={creating}>
              Start {mode === 'QUICK' ? 'Quick Proposal' : 'Customize Proposal'}
            </Button>
            <Button variant="secondary" onClick={() => setStep('template')} disabled={creating}>
              Back
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function StepIndicator({ step }: { step: Step }) {
  const steps: Array<{ id: Step; label: string }> = [
    { id: 'template', label: 'Select proposal' },
    { id: 'mode', label: 'Choose mode' },
  ];
  return (
    <ol className="flex items-center gap-2 text-[13px]">
      {steps.map((entry, index) => {
        const active = entry.id === step;
        const done = step === 'mode' && entry.id === 'template';
        return (
          <li key={entry.id} className="flex items-center gap-2">
            <span
              className={[
                'flex h-6 w-6 items-center justify-center rounded-full text-[12px] font-semibold',
                active
                  ? 'bg-teal text-white'
                  : done
                    ? 'bg-tint text-teal ring-1 ring-teal'
                    : 'bg-tint text-muted',
              ].join(' ')}
            >
              {done ? '✓' : index + 1}
            </span>
            <span className={active ? 'font-medium text-ink' : 'text-muted'}>{entry.label}</span>
            {index < steps.length - 1 && <span className="mx-1 text-rule">──</span>}
          </li>
        );
      })}
    </ol>
  );
}

function TemplateCard({
  template,
  selected,
  onSelect,
}: {
  template: TemplateSummary;
  selected: boolean;
  onSelect(): void;
}) {
  return (
    <label
      className={[
        'flex h-full cursor-pointer flex-col rounded-lg border p-4 transition-colors',
        selected
          ? 'border-teal bg-tint ring-1 ring-teal'
          : 'border-rule hover:border-muted hover:bg-tint',
      ].join(' ')}
    >
      <span className="flex items-start gap-3">
        <input
          type="radio"
          name="templateId"
          value={template.templateId}
          checked={selected}
          onChange={onSelect}
          className="mt-1 h-4 w-4 shrink-0 accent-teal"
        />
        <span className="text-sm font-semibold text-ink">{template.name}</span>
      </span>

      <span className="mt-2 block text-[13px] text-body">{template.description}</span>

      {template.bestFor.length > 0 && (
        <span className="mt-3 block border-t border-rule pt-3">
          <span className="block text-[11px] font-semibold uppercase tracking-wide text-teal">
            Best for
          </span>
          <ul className="mt-1.5 space-y-1">
            {template.bestFor.map((item) => (
              <li key={item} className="flex gap-1.5 text-[12px] text-body">
                <span aria-hidden="true" className="text-muted">
                  ·
                </span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </span>
      )}
    </label>
  );
}

function ModeCard({
  value,
  current,
  onSelect,
  title,
  description,
  includes,
  minutes,
  recommended,
}: {
  value: ProposalMode;
  current: ProposalMode;
  onSelect(mode: ProposalMode): void;
  title: string;
  description: string;
  includes: string[];
  minutes: string;
  recommended?: boolean;
}) {
  const selected = current === value;
  return (
    <label
      className={[
        'flex h-full cursor-pointer flex-col rounded-lg border p-5 transition-colors',
        selected
          ? 'border-teal bg-tint ring-1 ring-teal'
          : 'border-rule hover:border-muted hover:bg-tint',
      ].join(' ')}
    >
      <span className="flex items-start gap-3">
        <input
          type="radio"
          name="mode"
          value={value}
          checked={selected}
          onChange={() => onSelect(value)}
          className="mt-1 h-4 w-4 shrink-0 accent-teal"
        />
        <span className="min-w-0">
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-ink">{title}</span>
            {recommended && (
              <span className="rounded-full bg-teal px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                Recommended
              </span>
            )}
          </span>
          <span className="mt-0.5 block text-[12px] text-muted">{minutes}</span>
        </span>
      </span>

      <span className="mt-3 block text-[13px] text-body">{description}</span>

      <ul className="mt-3 space-y-1 border-t border-rule pt-3">
        {includes.map((item) => (
          <li key={item} className="flex gap-1.5 text-[12px] text-body">
            <span aria-hidden="true" className="text-teal">
              ✓
            </span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </label>
  );
}
